import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Card } from '@/components';
import { useApi } from '@/api/hooks';
import styles from './Analytics.module.css';

/* ── Types (API: docs/spec-ai-trend-analysis.md §6) ── */

interface DealScore {
  customer_id: string;
  name: string;
  status: string;
  source: string | null;
  score: number;
  risk: 'churn' | 'stalled' | 'no_contact' | null;
  action: string | null;
  last_contact_at: string | null;
  last_contact_days: number | null;
  viewing_count: number;
  contact_count_30d: number;
}

interface VacancyRow {
  id: string;
  title: string;
  customer_name: string | null;
  contract_end: string;
  notice_deadline: string;
  days_to_end: number;
  days_to_notice: number;
  tenure_months: number | null;
  short_tenure: boolean;
  amount: number | null;
  renewal_fee: number | null;
  property_name: string | null;
  level: 'critical' | 'warning' | 'info';
}

interface MinpakuAnalytics {
  quota: {
    listing_id: string;
    property_name: string | null;
    days_used: number;
    max_days: number;
    remaining: number;
    exceeded: boolean;
    warningLevel: 'normal' | 'warning' | 'critical' | 'exceeded';
  }[];
  ota: { ota: string; bookings: number; revenue: number; nights: number; adr: number }[];
  dow: { dow: number; bookings: number; revenue: number; nights: number; adr: number }[];
  avg_adr: number;
  suggestions: { type: string; message: string }[];
}

interface TenantConfig {
  enabled_modules: string[];
}

/* ── Labels ── */

const STAGE_LABEL: Record<string, string> = {
  new: '新規', interested: '興味あり', viewing: '内見', applied: '申込',
};

const RISK_LABEL: Record<string, { icon: string; label: string; cls: string }> = {
  churn: { icon: '🔴', label: '失注リスク', cls: 'riskChurn' },
  stalled: { icon: '🟠', label: '停滞', cls: 'riskStalled' },
  no_contact: { icon: '🟡', label: '接触なし', cls: 'riskNoContact' },
};

const LEVEL_LABEL: Record<string, { icon: string; label: string }> = {
  critical: { icon: '🔴', label: '予告期限が接近' },
  warning: { icon: '🟠', label: '更新交渉の時期' },
  info: { icon: '🟡', label: '予定として把握' },
};

const QUOTA_LABEL: Record<string, { icon: string; label: string }> = {
  exceeded: { icon: '🔴', label: '上限超過' },
  critical: { icon: '🔴', label: '残枠僅少' },
  warning: { icon: '🟠', label: '注意' },
  normal: { icon: '🟢', label: '余裕' },
};

const DOW_LABEL = ['日', '月', '火', '水', '木', '金', '土'];

const yen = (n: number) => `¥${n.toLocaleString('ja-JP')}`;

function scoreClass(score: number): string {
  if (score >= 70) return styles.scoreHigh;
  if (score >= 40) return styles.scoreMid;
  return styles.scoreLow;
}

/* ── Component ── */

export function Analytics() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'deals' | 'vacancy' | 'minpaku'>('deals');

  const { data: config } = useApi<TenantConfig>('/tenant/config');
  const { data: deals, loading: dLoading } = useApi<DealScore[]>('/tenant/analytics/deals');
  const { data: vacancy, loading: vLoading } = useApi<VacancyRow[]>('/tenant/analytics/vacancy');
  const { data: minpaku, loading: mLoading } = useApi<MinpakuAnalytics>('/tenant/analytics/minpaku');

  const hasMinpaku = (config?.enabled_modules ?? []).some(m => m.includes('minpaku'));

  const riskCount = (deals ?? []).filter(d => d.risk).length;
  const criticalCount = (vacancy ?? []).filter(v => v.level === 'critical').length;
  const renewalSum = (vacancy ?? [])
    .filter(v => v.days_to_end <= 90)
    .reduce((s, v) => s + (v.renewal_fee ?? 0), 0);

  return (
    <div className={styles.page}>
      {/* Tabs */}
      <div className={styles.tabs}>
        <button className={`${styles.tab} ${tab === 'deals' ? styles.tabActive : ''}`} onClick={() => setTab('deals')}>
          商談スコアリング {riskCount > 0 && <span className={styles.tabBadge}>{riskCount}</span>}
        </button>
        <button className={`${styles.tab} ${tab === 'vacancy' ? styles.tabActive : ''}`} onClick={() => setTab('vacancy')}>
          空室・更新アラート {criticalCount > 0 && <span className={styles.tabBadge}>{criticalCount}</span>}
        </button>
        {hasMinpaku && (
          <button className={`${styles.tab} ${tab === 'minpaku' ? styles.tabActive : ''}`} onClick={() => setTab('minpaku')}>
            民泊レベニュー
          </button>
        )}
      </div>

      {/* ── タブA: 商談スコアリング ── */}
      {tab === 'deals' && (
        <Card title="🎯 商談スコアリング">
          <p className={styles.lead}>
            進行中の見込み客をスコア順に表示します。<strong>リスクのある商談を優先</strong>して並べています。
          </p>
          {dLoading ? <p className={styles.muted}>読み込み中...</p>
            : !deals?.length ? <p className={styles.muted}>進行中の見込み客がありません</p>
            : (
              <div className={styles.list}>
                {deals.map(d => (
                  <div key={d.customer_id} className={styles.row} onClick={() => navigate(`/crm?id=${d.customer_id}`)}>
                    <div className={`${styles.score} ${scoreClass(d.score)}`}>{d.score}</div>
                    <div className={styles.rowMain}>
                      <div className={styles.rowHead}>
                        <span className={styles.rowName}>{d.name}</span>
                        <span className={styles.stage}>{STAGE_LABEL[d.status] ?? d.status}</span>
                        {d.source && <span className={styles.source}>{d.source}</span>}
                      </div>
                      {d.action && (
                        <div className={`${styles.action} ${styles[RISK_LABEL[d.risk!].cls]}`}>
                          {RISK_LABEL[d.risk!].icon} {d.action}
                        </div>
                      )}
                      <div className={styles.rowMeta}>
                        内見 {d.viewing_count}回 ・ 30日接触 {d.contact_count_30d}回
                        {d.last_contact_days !== null && ` ・ 最終接触 ${d.last_contact_days}日前`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </Card>
      )}

      {/* ── タブB: 空室・更新アラート ── */}
      {tab === 'vacancy' && (
        <Card title="🏠 空室・更新アラート">
          <p className={styles.lead}>
            <strong>解約予告期限</strong>を基準にしています。借主が解約を通知できる期限を過ぎると、その期の更新は確定します。
          </p>
          {vLoading ? <p className={styles.muted}>読み込み中...</p>
            : !vacancy?.length ? <p className={styles.muted}>直近180日以内に契約終了を迎える物件はありません</p>
            : (
              <>
                <div className={styles.summary}>
                  今後90日で更新対象 <strong>{vacancy.filter(v => v.days_to_end <= 90).length}件</strong>
                  ・想定更新料 <strong>{yen(renewalSum)}</strong>
                </div>
                <div className={styles.list}>
                  {vacancy.map(v => (
                    <div key={v.id} className={styles.row}>
                      <div className={styles.rowMain}>
                        <div className={styles.rowHead}>
                          <span className={styles.rowName}>{v.property_name ?? v.title}</span>
                          {v.customer_name && <span className={styles.source}>{v.customer_name}</span>}
                          {v.short_tenure && <span className={styles.shortTenure}>短期入居</span>}
                        </div>
                        <div className={`${styles.action} ${v.level === 'critical' ? styles.riskChurn : v.level === 'warning' ? styles.riskStalled : styles.riskNoContact}`}>
                          {LEVEL_LABEL[v.level].icon} {LEVEL_LABEL[v.level].label}
                          {v.days_to_notice >= 0
                            ? ` — 解約予告期限まで${v.days_to_notice}日`
                            : ` — 解約予告期限を${Math.abs(v.days_to_notice)}日経過`}
                        </div>
                        <div className={styles.rowMeta}>
                          契約終了 {v.contract_end}（あと{v.days_to_end}日）
                          ・予告期限 {v.notice_deadline}
                          {v.tenure_months !== null && ` ・入居${v.tenure_months}ヶ月`}
                          {v.amount ? ` ・賃料 ${yen(v.amount)}` : ''}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
        </Card>
      )}

      {/* ── タブD: 民泊レベニュー ── */}
      {tab === 'minpaku' && hasMinpaku && (
        <>
          <Card title="⚖️ 営業可能日数（180日規制）">
            <p className={styles.lead}>
              住宅宿泊事業法により年間の営業日数には上限があります。<strong>残枠は希少資源</strong>なので、高収益日に充てる判断が要ります。
            </p>
            {mLoading ? <p className={styles.muted}>読み込み中...</p>
              : !minpaku?.quota.length ? <p className={styles.muted}>民泊物件が登録されていません</p>
              : (
                <div className={styles.list}>
                  {minpaku.quota.map(q => (
                    <div key={q.listing_id} className={styles.row}>
                      <div className={styles.rowMain}>
                        <div className={styles.rowHead}>
                          <span className={styles.rowName}>{q.property_name ?? '(物件名なし)'}</span>
                          <span className={styles.stage}>
                            {QUOTA_LABEL[q.warningLevel].icon} {QUOTA_LABEL[q.warningLevel].label}
                          </span>
                        </div>
                        <div className={styles.quotaBar}>
                          <div className={styles.quotaFill}
                            style={{ width: `${Math.min((q.days_used / q.max_days) * 100, 100)}%` }} />
                        </div>
                        <div className={styles.rowMeta}>
                          {q.days_used} / {q.max_days}日 使用 ・ 残り{q.remaining}日
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </Card>

          {!!minpaku?.suggestions.length && (
            <Card title="💡 提案">
              <div className={styles.list}>
                {minpaku.suggestions.map((s, i) => (
                  <div key={i} className={styles.suggestion}>{s.message}</div>
                ))}
              </div>
            </Card>
          )}

          {!!minpaku?.ota.length && (
            <Card title="📊 OTA別実績">
              <table className={styles.table}>
                <thead>
                  <tr><th>OTA</th><th>予約</th><th>宿泊日数</th><th>売上</th><th>ADR</th></tr>
                </thead>
                <tbody>
                  {minpaku.ota.map(o => (
                    <tr key={o.ota}>
                      <td>{o.ota}</td>
                      <td>{o.bookings}件</td>
                      <td>{o.nights}泊</td>
                      <td>{yen(o.revenue)}</td>
                      <td>{yen(o.adr)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          {!!minpaku?.dow.length && (
            <Card title="📅 曜日別ADR（直近180日）">
              <p className={styles.lead}>平均 ADR: <strong>{yen(minpaku.avg_adr)}</strong></p>
              <div className={styles.dowGrid}>
                {minpaku.dow.map(d => (
                  <div key={d.dow} className={styles.dowCell}>
                    <div className={styles.dowLabel}>{DOW_LABEL[d.dow]}</div>
                    <div className={d.adr >= minpaku.avg_adr * 1.2 ? styles.dowHigh : styles.dowAdr}>
                      {yen(d.adr)}
                    </div>
                    <div className={styles.dowMeta}>{d.bookings}件</div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
